import { onAuthStateChanged, signInWithEmailAndPassword, signOut, User, createUserWithEmailAndPassword } from "firebase/auth";
import { getFirebaseAuth } from "./app";
import { createDocumentWithId, readDocument } from "./db";
import { Timestamp } from "firebase/firestore";
import { checkRateLimit, RATE_LIMITS } from "../utils/rateLimiter";
import { sendVerificationEmailCustom, sendPasswordResetEmailCustom } from "../services/emailService";

export interface AuthService {
  getCurrentUser: () => User | null;
  onAuthChange: (callback: (user: User | null) => void) => () => void;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  registerWithEmailOnly: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  getUserId: () => string | null;
  sendPasswordReset: (email: string) => Promise<void>;
}

let cachedUser: User | null = null;

export function createAuthService(): AuthService {
  const auth = getFirebaseAuth();

  const getCurrentUser = (): User | null => cachedUser || auth.currentUser;

  const onAuthChange = (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, (user) => {
      cachedUser = user;
      callback(user);
    });
  };

  const signInWithEmail = async (email: string, password: string): Promise<User> => {
    // Check rate limit for sign-in attempts
    checkRateLimit(RATE_LIMITS.SIGN_IN);
    
    const creds = await signInWithEmailAndPassword(auth, email, password);
    
    // Check if user is active before allowing login
    try {
      const userDoc = await readDocument<{ isActive?: boolean }>("user", creds.user.uid, false);
      if (userDoc && userDoc.isActive === false) {
        // User is inactive, sign them out and throw error
        await signOut(auth);
        cachedUser = null;
        throw new Error("Your account has been deactivated. Please contact support for assistance.");
      }
    } catch (error: any) {
      // If error is already about deactivation, re-throw it
      if (error.message && error.message.includes("deactivated")) {
        throw error;
      }
      // If document doesn't exist or other error, allow login (for backward compatibility)
      // but log the error
      console.warn("Could not check user active status:", error);
    }
    
    cachedUser = creds.user;
    return creds.user;
  };

  const registerWithEmailOnly = async (email: string): Promise<void> => {
    // Check rate limit for registration
    checkRateLimit(RATE_LIMITS.REGISTER);
    
    // Use deterministic password: 123@<local-part-of-email>
    const [localPart] = email.split("@");
    const password = `123@${localPart}`;
    const creds = await createUserWithEmailAndPassword(auth, email, password);
    await sendVerificationEmailCustom({ to: email, name: localPart || email });
    
    // Create user document in Firestore with Firebase Auth UID as document ID
    const name = localPart || email; // Use email local part as name, fallback to full email
    const userId = creds.user.uid;
    const userData = {
      id: userId,
      name: name,
      role: "user" as const,
      orders: 0,
      timestamp: Timestamp.now(),
      phoneNumber: null,
      email: email,
      isVerified: false,
      isActive: true,
    };
    
    await createDocumentWithId("user", userId, userData);
    
    // await signOut(auth);
    // cachedUser = null;
  };

  const signOutUser = async (): Promise<void> => {
    await signOut(auth);
    cachedUser = null;
  };

  const getIdToken = async (forceRefresh: boolean = false): Promise<string | null> => {
    const user = getCurrentUser();
    if (!user) return null;
    try {
      return await user.getIdToken(forceRefresh);
    } catch (error) {
      console.error("Error getting ID token:", error);
      return null;
    }
  };

  const getUserId = (): string | null => {
    const user = getCurrentUser();
    return user?.uid || null;
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    // Check rate limit for password reset
    checkRateLimit(RATE_LIMITS.PASSWORD_RESET);
    
    console.log("[Auth Service] Starting password reset for email:", email);
    
    try {
      await sendPasswordResetEmailCustom({ to: email });
      console.log("[Auth Service] Password reset email sent successfully via Resend");
    } catch (error: any) {
      console.error("[Auth Service] Error sending custom password reset:", error);
      throw error;
    }
  };

  return { 
    getCurrentUser, 
    onAuthChange, 
    signInWithEmail, 
    registerWithEmailOnly, 
    signOut: signOutUser,
    getIdToken,
    getUserId,
    sendPasswordReset
  };
}


